import { AfterContentInit, ChangeDetectorRef, Directive, ElementRef, HostListener, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControlDirective, FormControlName, NgModel, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { Calendar } from 'primeng/calendar';
import { Chips } from 'primeng/chips';
import { DataView } from 'primeng/dataview';
import { InputNumber } from 'primeng/inputnumber';
import { MultiSelect } from 'primeng/multiselect';
import { Panel } from 'primeng/panel';
import { TabPanel, Tabs } from 'primeng/tabs';
import { Subscription } from 'rxjs';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisSearchSelectPopupComponent } from '../../genericsearch/component/his-search.component';
import { HisDdDirectiveRegistryService } from '../service/directive/his-dd-directive-registry.service';

import { PickList } from 'primeng/picklist';
import { HisMultiselectComponent } from '../../input/component/multiselect/his-multiselect.component';
import { HisPlamComponent } from '../../structure/component/plam/his-plam.component';
import { HisTreeSelectComponent } from '../../tree/component/his-tree-select.component';
import { HisSummernoteComponent } from '../component/summernote/his-summernote.component';
import { ErrorMessage } from '../service/backend/his-backend.service';
import { HisDdService } from '../service/base/his-dd.service';
import { HisDateUtils } from '../utils';
import { HisDdValidator } from '../validator/his-dd-validator';
import { DdEntryMerged } from './../model/data-dictionary';
import { HisNotificationService } from './../service/notifications/his-notification.service';
import { HisDdContextsDirective } from './his-dd-contexts.directive';

/**
 * Looks up DataDictionary-Entries for the given Datadictionary-Key and decorates the given
 * Input-Element accordingly
 *
 * hisDdKey should be used on Input-Elements but partly works with other Elements as well (e.g. 'hidden')
 * ddKey may be changed dynamically
 */
@Directive( {
    selector: '[hisDdKey]',
    standalone: false
} )
export class HisDdDirective implements OnChanges, OnDestroy, AfterContentInit {
    private hisDdService = inject(HisDdService);
    private ddValidator = inject(HisDdValidator);
    private notificationService = inject(HisNotificationService);
    private hisUiService = inject(HisUiService);
    private elementRef = inject(ElementRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private ddDirectiveRegistryService = inject(HisDdDirectiveRegistryService);
    private ngModelRef = inject(NgModel, { host: true, optional: true, self: true });
    private ctrlRef = inject(FormControlDirective, { host: true, optional: true, self: true });
    private formControlName = inject( FormControlName, { host: true, optional: true } );
    private autocompleteRef = inject(AutoComplete, { host: true, optional: true, self: true });
    private calendarRef = inject(Calendar, { host: true, optional: true, self: true });
    private chipsRef = inject(Chips, { host: true, optional: true, self: true });
    private panelRef = inject(Panel, { host: true, optional: true, self: true });
    private multiSelectRef = inject(MultiSelect, { host: true, optional: true, self: true });
    private hisPlamRef = inject(HisPlamComponent, { host: true, optional: true, self: true });
    private hisTreeSelectRef = inject(HisTreeSelectComponent, { host: true, optional: true, self: true });
    private hisSearchSelectPopup = inject(HisSearchSelectPopupComponent, { host: true, optional: true, self: true });
    private inputNumber = inject(InputNumber, { host: true, optional: true, self: true });
    private dataViewRef = inject(DataView, { host: true, optional: true, self: true });
    private pickList = inject(PickList, { host: true, optional: true, self: true });
    private hisMultiselect = inject(HisMultiselectComponent, { host: true, optional: true, self: true });
    private tabs = inject(Tabs, { optional: true });
    private tabPanel = inject( TabPanel, { optional: true } );
    private hisEditor = inject( HisSummernoteComponent, { host: true, optional: true, self: true } );

    private readonly ddContextDir = inject( HisDdContextsDirective, { self: true });

    @Input() hisDdKey: string;
    /** @deprecated Use the the dedicated `HisDdContextsDirective` instead */
    set hisDdContexts( contexts: string[] | string ) {
        this.ddContextDir.setHisDdContexts( contexts );
    }
    get hisDdContexts(): string[] {
        return this.ddContextDir.getHisDdContexts();
    }
    @Input() ignoreDefaultValue = false;
    /**
     * Set the flag when the DD key is bound to a generic search criterion:
     * on the search mask only the hidden and readonly/disabled DD attributes
     * take effect. required, defaultValue and validators are ignored.
     */
    @Input() hisDdSearchCriterion = false;

    // Behandlung von AutoComplete-Defaults mit transformMethod
    @Input() hisDdTransformMethod: ( source: string, options?: any[] ) => any;
    private autocompleteDefaultValue: string; // Kann nur asynchron gesetzt werden, deshalb Zwischenspeicherung hier
    private autocompleteDefaultValueRetries = 5; // Einige Male probieren, den Default bei AutoCompletes zu setzen (während Optionen laden)

    // Damit der ddKey dynamisch geändert werden kann, werden hier die originalen Attribute gespeichert
    private originalValues: Record<string, any> = {};

    private fieldMessagesSubscription: Subscription;

    private originalRequiredValue: boolean = undefined;

    ngAfterContentInit() {
        /*
        * Field-Errors vom Backend empfangen.
        * Funktioniert bisher nur für template-driven Forms.
        * Validierungsfehler-Meldung wird für Reactive Forms
        * frontendseitig erzeugt.
        */

        localStorage.setItem( 'listTabs', '' );
        this.fieldMessagesSubscription = this.notificationService.fieldMessages$.subscribe(
            ( msg: ErrorMessage ) => {
                let errorElement: any = null;
                if ( this.ngModelRef != null && this.hisDdKey != null && this.hisDdKey === msg.field ) {
                    if ( this.ngModelRef.control.errors == null ) {
                        this.ngModelRef.control.setErrors( {} );
                    }
                    this.ngModelRef.control.errors.backendError = { message: msg.message, valid: false };

                    // Fokus auf Eingabefeld mit Fehlermeldung setzen.
                    // TODO: Autocomplete und Calendar unterstützen
                    // TODO: nur auf erste Fehlermeldung setzen, für die ein Eingabefeld gefunden wird.
                    //this.elementRef.nativeElement.focus();

                    errorElement = this.elementRef.nativeElement?.closest( '.plamRow' );
                } else if ( this.ngModelRef == null
                     && this.elementRef.nativeElement !== null
                     && this.hisDdKey != null
                     && this.hisDdKey.startsWith( 'dynamic.' )
                     && this.hisDdKey === msg.field ) {
                    // Das this.ngModelRefist für/bei dynamischen Attributen null,
                    // daher müssen wir die Feldermeldungen anderweitig behandeln und setzen
                    this.setDdBackendErrorForDynamicFields( msg.message );

                    errorElement = this.elementRef.nativeElement?.closest( '.plamRow' );
                }
                if ( errorElement !== null ) {
                    HisDdDirective.removeErrorIconInTab();
                    setTimeout( () => {
                        this.markTabsWithError( errorElement );
                    }, 0.01 );
                }
            }
        );
        // register hisDdDirectivce
        this.ddDirectiveRegistryService.registerHisDdDirective( this );
    }

    // Setzen des Warnicons in der Registerkarte
    public markTabsWithError( errorElement: any ) {
        if ( errorElement ) {
            let tabHeader = errorElement.closest( 'p-tabpanel' )?.getAttribute( 'aria-labelledby' );
            if ( tabHeader ) {
                /* Überprüfung, ob fehlerhafte Registerkarte bereits ein Warnicon hat.
                Wenn nicht, dann markTabsWithError zum Setzen des Warnicons aufrufen */
                let markedTabs = localStorage.getItem('listTabs') || '';
                if ( !markedTabs.includes( tabHeader ) && markedTabs !== undefined ) {
                    let tabs: any = document.getElementsByClassName( 'p-tab' );
                    for ( let tab of tabs ) {
                        if ( tab.id === tabHeader ) {
                            const tabTitleElement = (Array.from(tab.childNodes) as Node[]).find((n: Node) =>
                                n.nodeType === Node.TEXT_NODE || n.nodeType === Node.ELEMENT_NODE
                            );
                            // insert "warning"-icon on tab, if not already on exists
                            if ( !tab.querySelector('.tabWarnIcon') ) {
                                const warningIcon = document.createElement('img');
                                warningIcon.src = '/HISinOne/images/icons/bullet_error.svg';
                                warningIcon.className = 'tabWarnIcon marginRightStandard';
                                warningIcon.title = this.hisUiService.translate('common.error.tab');
                                warningIcon.alt = this.hisUiService.translate('common.error.tab');
                                // Füge das Icon vor dem eigentlichen Tab-Inhalt ein
                                tab.insertBefore(warningIcon, tabTitleElement);
                            } else {
                                const text = (tabTitleElement as HTMLElement)?.innerText;
                                console.log( '#HisDdDirective.markTabsWithError() tab - ', text, ' - already contains warning tab icon.');
                            }
                        }
                    }
                    // Fokus auf die erste Registerkarte mit einem Fehler setzen
                    if( tabs ) {
                        for(let tab of tabs) {
                            if(tab.querySelector('.tabWarnIcon')){
                                tab.focus();
                                break;
                            }
                        }
                    }
                    let warnIcons: any = document.getElementsByClassName( 'tabWarnIcon' );
                    for ( let icon of warnIcons ) {
                        icon.title = this.hisUiService.translate( 'common.error.tab' );
                        icon.alt = this.hisUiService.translate( 'common.error.tab' );
                    }
                    let markedTabsArray = markedTabs.split(',').filter(Boolean);
                    if (!markedTabsArray.includes(tabHeader)) {
                        markedTabsArray.push(tabHeader);
					    localStorage.setItem('listTabs', markedTabsArray.join(','));
                    }
                }
            }
        }
    }

    public static removeErrorIconInTab() {
        let tabErrorIcons: any = document.getElementsByClassName( 'tabWarnIcon' );
        do {
            for ( let icon of tabErrorIcons ) {
                icon.parentNode.removeChild( icon );
                localStorage.setItem( 'listTabs', '' );
            }
        } while ( tabErrorIcons.length > 0 );
    }

    /**
     * Reagiere auf Änderungen des ddKey (ermöglicht dynamische Änderung)
     */
    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.hisDdKey || changes.hisDdContexts ) {
            this.notifyDdKeyOrContextsChanged();
            if ( this.autocompleteDefaultValue != null ) {
	            this.handleAutocompleteDefaultValue(undefined);
	        }
        }

    }

    ngOnDestroy(): void {
        if ( this.fieldMessagesSubscription ) {
            this.fieldMessagesSubscription.unsubscribe();
        }
    }

    @HostListener( 'ngModelChange', ['$event'] )
    onNgModelChange( event: any ) {
        if ( !this.autocompleteRef || this.autocompleteRef && ( typeof event !== 'string' ) ) {
            setTimeout( () => {
                this.ddDirectiveRegistryService.checkForContextChanges( this.hisDdKey );
            }, 1 ); // delay registery-service call to be sure ngModel was updated
        }
        // reset ddBackenErrors for dynamic field/attribute
        this.setDdBackendErrorForDynamicFields( null );
    }

    /**
     * When hisDataSource sets suggestions via AutoCompleteRef, change detection is not triggered.
     * This custom event is called instead.
     */
    @HostListener( 'hisDataSourceDataAvailable', ['$event'] )
    onHisDataSourceDataAvailable( event: Event ) {
        if ( this.autocompleteDefaultValue != null ) {
            this.handleAutocompleteDefaultValue(event);
        }
    }

    /**
     * Setzt den Autocomplete-Defaultwert, nachdem die Gesamtliste der Optionen
     * in 'suggestions' zur Verfügung steht.
     */
    handleAutocompleteDefaultValue(event: Event, dDReadOnly?: boolean): any {
        if ( this.autocompleteRef == null || this.ignoreDefaultValue) {
            return;
        }
        let defaultValue = this.autocompleteDefaultValue;
        let suggestions = this.autocompleteRef.suggestions;

        if ( suggestions != null && suggestions.length > 0 ) {
            this.autocompleteDefaultValue = null; // Verhindern, dass der selbe DefaultWert noch einmal behandelt wird.
            // Default nur setzen, wenn vorher noch kein Wert gesetzt war (leeres Objekt (bei multiselect) gilt als kein Wert)
            // oder ein Defaultwert existiert und das Feld readOnly definiert ist (Weiterführung der Abfrage-Bedingung aus applyDefaultValueIfSet())
            if ( this.modelControl && ( ( this.modelControl.value == null || !Object.keys( this.modelControl.value ).length )
                || ( this.modelControl.value !== null && dDReadOnly ) ) ) {
                if ( this.hisDdTransformMethod ) {
                    defaultValue = this.hisDdTransformMethod( defaultValue, suggestions );
                } else {
                    defaultValue = this.hisDdDefaultTransformMethod( defaultValue, suggestions );
                }

                if (defaultValue !== null) {
                    this.setModelValue( defaultValue );
                }
            }
        } else {
            // Die Optionen waren (noch) leer - später nochmal versuchen
            if ( this.autocompleteDefaultValueRetries-- > 0 ) {
                setTimeout(
                    () => {
                        this.autocompleteRef.completeMethod.emit(
                            { query: '', originalEvent: event } ); // Alle Optionen / suggestions anfordern
                    },
                    200 );
            }
        }
    }

    hisDdDefaultTransformMethod( defaultValue: string, entries: any[] ): any {
        for ( let entry of entries ) {
            if ( entry.objGuid === defaultValue
                || entry.hisKeyId === defaultValue
                || entry === defaultValue ) {

                return entry;
            }
        }
        return null;
    }

    /** Returns the current DD contexts. Prefer using `getHisDdContexts` from the `HisDdContextsDirective` directly if possible. */
    public getDdContexts(): string[] {
        return this.ddContextDir.getHisDdContexts();
    }

    /**
     * Der ddKey oder ddContexts hat sich geändert -> nun müssen die neuen Werte gesetzt
     * werden, falls ein Entry existiert
     */
    notifyDdKeyOrContextsChanged() {
        let elInput = this.findElementRef();
        if ( !elInput ) {
            return;
        }

        // Zunächst Fehlermeldungen von alten Kontexten löschen und eventuelle Originalwerte wiederherstellen
        this.resetErrors();
        this.restoreOriginalValues( elInput );

        // Wenn ddKey gesetzt, Werte anwenden
        if ( this.hisDdKey ) {
            elInput.hisDdContexts = this.getDdContexts();

            let ddEntry = this.hisDdService.getMergedEntry( this.hisDdKey, this.getDdContexts() );
            if ( !ddEntry || !ddEntry.identifier ) {
                // Minimalen Entry synthetisieren, wenn nicht vorhanden, um Label zu erhalten
                ddEntry = { identifier: this.hisDdKey, hidden: undefined };
            }

            // Setzen der dataSourceFilter an AutoComplete/Auswahlfeldern
            if ( elInput instanceof AutoComplete ) {
                if ( ddEntry.dataSourceFilter ) {
                    // Current expected format { hiskeyIds = 1001,1004 } || { objGuids = c93bc42b-ba6d-48c4-9047-8a0bb6a69d92, a798722d-b1fa-4f19-8238-1845f1938cfc }
                    ( this.autocompleteRef as any ).dataSourceFilter = ddEntry.dataSourceFilter;
                } else {
                    ( this.autocompleteRef as any ).dataSourceFilter = undefined;
                }
            }
            this.applyDdEntry( ddEntry, elInput );
        }
    }

    /**
     * Um die Validieren auf Pflichtfeld für Übersetzungsfelder zu deaktivieren
     * könnten wir hier das required-Attribut auf false setzen, bzw. es auf seinen
     * Ursprungswert zurücksetzen
     */
    forceNotRequired( forceRequiredFalse: boolean ) {
        let elInput = this.findElementRef();
        if ( !elInput && !this.isReactiveForm() ) {
            return;
        }

        // Wenn ddKey gesetzt, Werte anwenden
        if ( this.hisDdKey ) {
            let ddEntry = this.hisDdService.getMergedEntry( this.hisDdKey, this.getDdContexts() );
            if ( !this.originalRequiredValue ) {
                this.originalRequiredValue = ddEntry.required;
            }
            if ( forceRequiredFalse ) {
                // force required false
                ddEntry.required = false;
            } else {
                ddEntry.required = this.originalRequiredValue;
            }

            this.applyDdEntry( ddEntry, elInput );
        }
    }

    resetErrors() {
        if ( this.modelControl?.errors ) {
            console.log('delete error messages for hisDdKey', this.hisDdKey);
            this.modelControl.setErrors( null ); // bei {} würden noch Fehler ohne Meldung angezeigt werden
        }
    }

    private setDdBackendErrorForDynamicFields( ddBackendErrorMessage: string ) {
        if ( this.ngModelRef == null && ( this.hisDdKey && this.hisDdKey.startsWith( 'dynamic.' ) ) ) {
            if ( this.autocompleteRef ) {
                ( this.autocompleteRef as any ).ddBackendError = ddBackendErrorMessage;
            } else if ( this.calendarRef ) {
                ( this.calendarRef as any ).ddBackendError = ddBackendErrorMessage;
            } else {
                this.elementRef.nativeElement.ddBackendError = ddBackendErrorMessage;
            }
        }
    }

    /**
     * Um dynamische Änderung zu erlauben, müssen die Originalwerte
     * vor der Anwendung des vorherigen DdEntries wieder hergestellt werden können
     */
    restoreOriginalValues( elInput: any ) {
        for ( let key in this.originalValues ) {
            elInput[key] = this.originalValues[key];
        }
    }

    /**
     * Übernimmt einen Wert aus dem ddEntry in das Inputfeld, falls gesetzt.
     * Vorher wird der alte Wert in den originalValues zwischengespeichert.
     * Wenn sich der Zustand 'ungesetzt' nicht einfach durch Setzen des alten Wertes wieder herstellen lässt,
     * kann ein Ersatzwert für 'ungesetzt' angegeben werden.
     */
    applyPropertyIfSet(
        ddAttributeValue: string | boolean | number, elInput: any,
        inputProperty: string, unsetValue?: any, unsetSubst?: any ) {
        if ( ddAttributeValue != null && ddAttributeValue !== false ) {
            if ( unsetValue != null && elInput[inputProperty] === unsetValue ) {
                this.originalValues[inputProperty] = unsetSubst;
            } else {
                this.originalValues[inputProperty] = elInput[inputProperty];
            }
            elInput[inputProperty] = ddAttributeValue;
        }
    }

    /**
     * Übernimmt minDate-Angaben, wenn im DD gesetzt.
     * Dabei wird zwischen 'input type=date' und der p-calendar-Komponente unterschieden.
     *
     * @param rawDate Datum als ISO-String oder in Millisekunden seit 1.1.1970 00:00:00 UTC
     */
    applyMinDatePropertyIfSet( rawDate: string | number, elInput: any ) {
        if ( rawDate !== null && rawDate !== undefined ) { // rawDate===0 ist ein gültiges Datum
            if ( elInput.type === 'date' ) {
                this.originalValues.min = elInput.min;
                elInput.min = HisDateUtils.convertToIsoDateString( rawDate );
            } else if ( elInput instanceof Calendar ) {
                this.originalValues.minDate = ( elInput as Calendar ).minDate;
                ( elInput as Calendar ).minDate = HisDateUtils.convertToLocalDate( rawDate );
            }
        }
    }

    /**
     * Übernimmt maxDate-Angaben, wenn im DD gesetzt.
     * Dabei wird zwischen 'input type=date' und der p-calendar-Komponente unterschieden.
     *
     * @param rawDate Datum als ISO-String oder in Millisekunden seit 1.1.1970 00:00:00 UTC
     */
    applyMaxDatePropertyIfSet( rawDate: string | number, elInput: any ) {
        if ( rawDate !== null && rawDate !== undefined ) { // rawDate===0 ist ein gültiges Datum
            if ( elInput.type === 'date' ) {
                this.originalValues.max = elInput.max;
                elInput.max = HisDateUtils.convertToIsoDateString( rawDate );
            } else if ( elInput instanceof Calendar ) {
                this.originalValues.maxDate = ( elInput as Calendar ).maxDate;
                ( elInput as Calendar ).maxDate = HisDateUtils.convertToLocalDate( rawDate );
            }
        }
    }

    /**
     * Wendet einen im DD gesetzten Standardwert auf die Eingabekomponente an,
     * sofern noch kein Wert gesetzt wurde.
     * TODO: Funktioniert noch nicht für Reactive Forms
     */
    private applyDefaultValueIfSet( ddEntry: DdEntryMerged, elInput: any ) {
        // defaultValue setzen, wenn noch kein Wert gesetzt
        if ( ddEntry.defaultValue == null ) {
            return;
        }
        if ( !this.modelControl ) {
            return;
        }
        if ( this.modelControl.value != null && ( ddEntry.defaultValue == this.modelControl.value || !ddEntry.readonly ) ) {
            return;
        }

        let defaultValue: string | Date = ddEntry.defaultValue;

        if ( elInput instanceof HisMultiselectComponent ) {
            // applying default value is not supported for multiselect yet
        } else if ( elInput instanceof AutoComplete ) {
            // DefaultValue asynchron setzen, nachdem die Optionen ermittelt wurden
            // Das läuft in ngOnChanges über die Veränderung von 'suggestions'
            this.autocompleteDefaultValue = defaultValue;
            this.handleAutocompleteDefaultValue( undefined, ddEntry.readonly );
            // Verzögern bis nach diesem Darstellungszyklus, da sonst der geänderte Wert
            // von 'suggestions' angemeckert wird
            setTimeout( () => {
                this.autocompleteRef.completeMethod.emit(
                    { query: '', originalEvent: undefined } ); // Alle Optionen / suggestions anfordern
            } );
        } else {
            if ( this.hisDdTransformMethod ) {
                defaultValue = this.hisDdTransformMethod( defaultValue );
            }

            if ( elInput instanceof Calendar ) {
                try {
                    defaultValue = HisDateUtils.convertToLocalDate( defaultValue );
                } catch ( error ) {
                    console.warn( 'Could not transform Default-String to Date (ISO Date expected): \'' + defaultValue + '\'' );
                    defaultValue = null;
                }
            }

            setTimeout( () => {
                this.setModelValue( defaultValue );
            } );
        }
    }

    /**
     * Wendet den Entry auf das (Input-) Element an
     */
    applyDdEntry( ddEntry: DdEntryMerged, elInput: any ) {
        // Generic search criteria: only hidden and readonly/disabled take effect
        if ( this.hisDdSearchCriterion ) {
            ddEntry = this.restrictDdEntryForSearchCriterion( ddEntry );
        }

        // zunächst die Eigenschaften anwenden, die auch bei passiven Komponenten
        // (nicht-Eingabefelder) greifen sollen.
        this.applyPropertyIfSet( ddEntry.helptextIdentifier, elInput, 'ddHelptext' );
        this.applyPropertyIfSet( ddEntry.backendErrorIdentifier, elInput, 'ddBackendError' );

        // labeltextIdentifier überschreibt den identifier bzgl. Label-Oberflächenschlüssel
        if ( ddEntry.labeltextIdentifier != null ) {
            this.applyPropertyIfSet( ddEntry.labeltextIdentifier, elInput, 'ddLabeltext' );
        } else {
            this.applyPropertyIfSet( ddEntry.identifier, elInput, 'ddLabeltext' );
        }

        this.applyPropertyIfSet( ddEntry.identifier, elInput, 'ddIdentifier' );

        // hidden
        if ( this.isHidden( ddEntry ) ) {
            this.hideInputField( elInput, ddEntry );
            this.ddDirectiveRegistryService.resetObjectValue( ddEntry.identifier );
        } else {
            this.showChildInputField( elInput, ddEntry );
        }

        this.applyPropertyIfSet( ddEntry.required, elInput, 'required' );

        // Ab hier werden Werte für Eingabekomponenten gesetzt.
        // Deshalb abbrechen, wenn kein ngModel vorhanden ist
        if ( !this.ngModelRef && !this.isReactiveForm() ) {
            return;
        }

        // defaultValue setzen, wenn noch kein Wert gesetzt
        // Das soll auch bei hidden Eingabefeldern passieren können
        this.applyDefaultValueIfSet( ddEntry, elInput );

        // wenn hidden insb. keine Validatoren an die Eingabe binden
        if ( this.isHidden( ddEntry ) ) {
            return;
        }

        // setzen des readonly Wertes für Eingabefelder
        this.setReadOnly( elInput, ddEntry );

        // Bei Reactive Forms ist required ein Validator und keine Property.
        if ( !this.isReactiveForm() ) {
            // Hack, da -1 in 64000 übersetzt wird! Aber -1 lässt sich nicht wieder setzen und null wird als 0 angesehen,
            // was ein schlechter max-Wert ist ;)
            this.applyPropertyIfSet( ddEntry.maxLength, elInput, 'maxLength', -1, 64000 );
            // Hack, da -1 in 0 übersetzt wird! Aber -1 lässt sich nicht wieder setzen
            this.applyPropertyIfSet( ddEntry.minLength, elInput, 'minLength', -1, 0 );
        }

        this.applyMinDatePropertyIfSet( ddEntry.minValueDate, elInput );
        this.applyMaxDatePropertyIfSet( ddEntry.maxValueDate, elInput );

        // Validatoren erzeugen
        if ( ddEntry.datatype === 'PAST_DATE' ) {
            this.applyMaxDatePropertyIfSet( new Date().toISOString(), elInput );
        } else if ( ddEntry.datatype === 'FUTURE_DATE' ) {
            this.applyMinDatePropertyIfSet( new Date().toISOString(), elInput );
        }

        this.ddValidator.registerValidators( ddEntry );

        let vals: ValidatorFn[] = this.ddValidator.getValidators();
        if ( this.isReactiveForm() ) {
            this.formControl.addValidators( Validators.compose( vals ) );
            this.formControl.updateValueAndValidity();
        } else {
            this.ngModelRef.control.addValidators( Validators.compose( vals ) );
            this.ngModelRef.control.updateValueAndValidity();
        }
    }

    /**
     * Reduces the DD entry of a generic search criterion to the attributes that
     * are allowed on the search mask: hidden and readonly/disabled.
     */
    private restrictDdEntryForSearchCriterion( ddEntry: DdEntryMerged ): DdEntryMerged {
        const restricted = new DdEntryMerged();
        restricted.identifier = ddEntry.identifier;
        restricted.hidden = ddEntry.hidden;
        restricted.active = ddEntry.active;
        return restricted;
    }

    private hideInputField( elInput: any, ddEntry: DdEntryMerged ) {
        this.originalValues.style = elInput.style;
        this.originalValues.ddHidden = elInput.ddHidden;
        elInput.ddHidden = this.isHidden( ddEntry );
        if ( elInput.style == null ) {
            elInput.style = {};
        }
        elInput.style.display = 'none';
    }

    private showChildInputField( elInput: any, ddEntry: DdEntryMerged ) {
        if( elInput.el?.nativeElement?.firstElementChild ) {
            elInput.el.nativeElement.firstElementChild.style.display = '';
        } else {
            this.elementRef.nativeElement.ddHidden = this.isHidden( ddEntry );
        }
    }

    private setReadOnly( elInput: any, ddEntry: DdEntryMerged ) {
        if ( elInput instanceof Calendar || elInput instanceof Chips || elInput instanceof AutoComplete  || elInput instanceof DataView ) {
            // Calendar, Autocomplete, Chips und DataView verstehen kein readonly und müssen deshalb disabled werden
            this.applyPropertyIfSet( ddEntry.readonly, elInput, 'disabled' );
            // markForCheck to trigger ChangeDection, um gesetzte Attribute wie "readOnly" / "disabled" zu reseten/entfernen.
            this.changeDetectorRef.markForCheck();
        } else if ( elInput instanceof InputNumber) {
            this.applyPropertyIfSet( ddEntry.readonly, elInput, 'readonly' );
        } else if ( elInput instanceof HisSummernoteComponent ) {
            elInput.setDisabledState( ddEntry.readonly );//this.( ddEntry.readonly, elInput, 'readonly' );
        } else {
            this.applyPropertyIfSet( ddEntry.readonly, elInput, 'readOnly' );
        }
    }

    /**
     * find element reference of this directive is defined on
     */
    public findElementRef(): any {
        if ( this.calendarRef ) {
            return this.calendarRef;
        } else if ( this.autocompleteRef ) {
            return this.autocompleteRef;
        } else if ( this.chipsRef ) {
            return this.chipsRef;
        } else if ( this.panelRef ) {
            return this.panelRef;
        } else if ( this.multiSelectRef ) {
            return this.multiSelectRef;
        } else if ( this.hisPlamRef ) {
            return this.hisPlamRef;
        } else if ( this.hisTreeSelectRef ) {
            return this.hisTreeSelectRef;
        } else if ( this.inputNumber ) {
            return this.inputNumber;
        } else if ( this.hisSearchSelectPopup ) {
            return this.hisSearchSelectPopup;
        } else if ( this.dataViewRef ) {
            return this.dataViewRef;
        } else if ( this.pickList ) {
            return this.pickList;
        } else if ( this.hisMultiselect ) {
            return this.hisMultiselect;
        } else if ( this.hisEditor ) {
            return this.hisEditor;
        }
        return this.elementRef.nativeElement;
    }

    /**
     * Setzt einen neuen Wert im Model und benachrichtigt auch die View über den geänderten Wert,
     * so dass dieser dargestellt wird.
     */
    public setModelValue( newValue: any ) {
        // Diese Art, das Modell zu setzen aktualisiert die View und das Model
        // Das war Ergebnis harter experimenteller Arbeit - bitte nich leichtfertig ändern ;) JOL
        this.modelControl.reset( newValue );

        if ( this.ngModelRef ) {
            this.ngModelRef.viewToModelUpdate( newValue );
        }

        if ( this.autocompleteRef ) {
            this.autocompleteRef.onSelect.emit( {
                originalEvent: new CustomEvent('custom'),
                value: newValue,
            });
        }
    }

    private get formControl(): UntypedFormControl {
        return this.ctrlRef?.form ?? this.formControlName?.formDirective?.form;
    }

    private get modelControl(): UntypedFormControl {
        return this.ngModelRef?.control ?? this.formControlName?.control;
    }

    private isReactiveForm(): boolean {
        return !!this.formControl;
    }
    
    private isHidden(ddEntry: DdEntryMerged): boolean {
        return ddEntry.hidden;
    }
}
